/**
 * OpportunitiesView.jsx - the Opportunities tab: the bid pipeline for the
 * selected year (open / won / lost / cancelled / no quote) in a filterable table.
 *
 * An opportunity and a project are the SAME record - only the status differs.
 * Won work is listed on the Projects tab (src/data/projectStatus.js). The
 * editor itself lives in components/opportunities/.
 */
import { useState, useEffect, useRef } from 'react';
import { Toast } from '../components/ui/Toast';
import { OpportunitiesTable } from '../components/OpportunitiesTable';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { EditOpportunityModal } from '../components/opportunities/EditOpportunityModal';
import AiCreateButton from '../components/aiCreate/AiCreateButton';
import { useCloseOpportunity } from '../components/opportunities/useCloseOpportunity';
import { useDataScope } from '../context/DataScope';

// Re-exported so the CRM and anything else importing these from the view keep working.
export { EditOpportunityModal } from '../components/opportunities/EditOpportunityModal';
export { ConfirmStatusModal } from '../components/opportunities/ConfirmStatusModal';

function parseHashProject() {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  if (qIdx < 0) return null;
  const params = new URLSearchParams(hash.slice(qIdx + 1));
  return params.get('project') || null;
}

/* ===== Main View ===== */
export default function OpportunitiesView() {
  const { opportunitiesThisYear, allProjects, updateProject, selectedYear } = useApp();
  const { user } = useAuth();
  const scope = useDataScope(); // set on the GLOBAL KSA tab, which reuses this view
  // On KSA the Projects view below owns ?project= links; opening this editor
  // too would leave a second, hidden modal behind the project detail.
  const access = useMyAccess();
  const editable = canEdit(access, 'opportunities');
  const allOpps = opportunitiesThisYear || [];

  // "My" vs "All" is a client-side view filter (not access control) — both BDs
  // can switch to All and see everyone's book. Default All so the populated
  // view shows first.
  const [view, setView] = useState('all');
  const myEmail = (user?.email || '').toLowerCase();
  const sourceOpps = view === 'mine' && myEmail ? allOpps.filter((o) => (o.owner || '').toLowerCase() === myEmail) : allOpps;

  // Auto-open the edit modal when arriving with ?project=<id> on the hash.
  // Look in allProjects (not opportunitiesThisYear) because the deep-link
  // might point to an opp outside the currently-filtered year.
  const [editing, setEditing] = useState(() => {
    const id = scope ? null : parseHashProject();
    return id ? (allProjects || []).find((p) => p.id === id) || null : null;
  });
  const [toast, setToast] = useState(null);
  // The quick Won / Lost / Cancelled buttons: each confirms, and Won then asks
  // for the PO (WonProjectSetup) instead of making someone hunt the row down on
  // the Projects tab. Shared with an account's Opportunities tab.
  const closeFlow = useCloseOpportunity({ updateProject, onToast: setToast });

  useEffect(() => {
    const onHash = () => {
      const id = scope ? null : parseHashProject();
      if (id) {
        const opp = (allProjects || []).find((p) => p.id === id);
        if (opp) setEditing(opp);
      }
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, [allProjects, scope]);

  // Cold-load deep link: on first page load the data isn't fetched yet, so the
  // initial editing state can't find the ?project=<id> row. Once allProjects
  // arrives, open it once (ref guard prevents re-opening after the user closes
  // the modal and a later data refresh fires).
  const deepLinkHandledRef = useRef(false);
  useEffect(() => {
    if (deepLinkHandledRef.current || editing) return;
    const id = scope ? null : parseHashProject();
    if (!id) return;
    const opp = (allProjects || []).find((p) => p.id === id);
    if (opp) {
      setEditing(opp);
      deepLinkHandledRef.current = true;
    }
  }, [allProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  const yearLabel = selectedYear === 'all' ? 'All Time' : Array.isArray(selectedYear) ? selectedYear.join(' + ') : String(selectedYear);

  return (
    <div className="space-y-4">
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
      {closeFlow.modals}
      {/* keyed by record: a ?project= link to another opportunity while this one is open
          mounts a fresh editor, so Save can never write one opportunity's form onto another */}
      {editing && (
        <EditOpportunityModal
          key={editing.id || 'new'}
          opp={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setToast('Opportunity updated')}
        />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-eyebrow uppercase text-text-muted mb-1">
            {scope ? `${scope.label} · Opportunities` : 'Workspace · Opportunities Pipeline'}
          </div>
          <p className="text-body-sm text-text-body m-0">
            <b className="text-text-dark tabular-nums">{sourceOpps.length}</b> {view === 'mine' ? 'of my' : ''} opportunities in {yearLabel}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* My | All view toggle — client-side owner filter */}
          <div style={{ display: 'inline-flex', borderRadius: 8, border: '1px solid #D8DED4', overflow: 'hidden' }}>
            {[
              ['mine', 'My Opportunities'],
              ['all', 'All Opportunities'],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setView(key)}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 11.5,
                  fontWeight: 700,
                  fontFamily: "'Montserrat', sans-serif",
                  background: view === key ? '#1B5E20' : '#fff',
                  color: view === key ? '#fff' : '#555',
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Create from screenshot / text: never on the KSA tab, which has no CRM
              accounts (it checks Edit on Accounts / Opportunities itself) */}
          {!scope && <AiCreateButton mode="opportunities" style={{ padding: '8px 14px', borderRadius: 8, fontSize: 11.5 }} />}

          {/* Create a new opportunity (workspace-level; customer chosen in the form,
              owner auto-set to the signed-in user). */}
          {editable && (
            <button
              type="button"
              onClick={() => setEditing({})}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 700,
                fontFamily: "'Montserrat', sans-serif",
                background: '#1B5E20',
                color: '#fff',
                whiteSpace: 'nowrap',
              }}
            >
              ＋ New Opportunity
            </button>
          )}
        </div>
      </div>

      <OpportunitiesTable
        opps={sourceOpps}
        onEdit={setEditing}
        onMarkWon={closeFlow.markWon}
        onMarkLost={closeFlow.markLost}
        onMarkCancelled={closeFlow.markCancelled}
        onMarkNoQuote={closeFlow.markNoQuote}
        updateProject={updateProject}
        onToast={setToast}
        defaultStatus="Open"
        tableKey={scope ? 'ksa-opportunities' : 'opportunities'}
      />
    </div>
  );
}
